/* eslint-disable react-refresh/only-export-components -- the drawer and the sender line the log's header shares */
import { useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useDeviationDetail, useSaveDeviationReview } from '@/hooks/use-deviations';
import { usePermissions } from '@/hooks/use-permissions';
import { useResponseSources } from '@/hooks/use-response-sources';
import { humanize } from '@/lib/automation-replies';
import { cn } from '@/lib/utils';
import { markSentenceChanges, markWordChanges, type SentenceChange } from '@/lib/word-diff';
import { StatusTag } from '@/modules/settings';
import { DEVIATION_REVIEW_VERDICTS, type Deviation, type DeviationDetail, type DeviationReviewVerdict } from '@/services/deviation.service';
import { AiScoreCell, SENT_VIA_LABELS, WRITING_KIND_LABELS } from './deviation-cells';

/**
 * The body of one deviation's drawer, in bands split by full-width lines: why it was sent anyway and
 * the facts, then what the customer asked, the AI draft against what was sent, the sources the draft
 * used, the scores and the reviewer's verdict. Anyone who can open the log saves a verdict and a note.
 */

const MAX_NOTE_LENGTH = 1000;
/** A factor at this level or under is what paused the Send. */
const LOW_FACTOR_LEVEL = 2;

/** The select popover's surface: white, a thin border, small radius. */
const BOX_CLASS = 'rounded-md border border-gray-300 bg-white';
const TEXT_BOX_CLASS = cn(BOX_CLASS, 'whitespace-pre-wrap px-3 py-2 text-[13px] leading-[20px] text-ink');

const VERDICT_CHOICES: Record<DeviationReviewVerdict, { title: string; hint: string }> = {
	kim: { title: 'Kim should learn this', hint: 'The agent reply is the better answer' },
	agent: { title: 'Agent went off policy', hint: 'Keep Kim as it is, coach the agent' },
	style: { title: 'Style only', hint: 'Same outcome, different wording' },
	both: { title: 'Both fine', hint: 'Either reply is acceptable' }
};

export const sentByText = (deviation: Pick<Deviation, 'sentBy' | 'sentVia'>): string => {
	const sender = deviation.sentBy ?? 'Unknown sender';
	return deviation.sentVia === 'workspace' ? `Sent by ${sender} from the Sentinel workspace` : `Sent by ${sender} through the extension`;
};

/** One band of the drawer: a small heading (and an optional control) above its content. */
function Section({ label, aside, children }: { label: string; aside?: ReactNode; children: ReactNode }) {
	return (
		<section aria-label={label} className='flex min-w-0 flex-col gap-2.5 px-5 py-4'>
			<div className='flex min-h-6 flex-wrap items-center justify-between gap-2'>
				<h3 className='text-[12px] font-medium text-gray-800'>{label}</h3>
				{aside}
			</div>
			{children}
		</section>
	);
}

function MetaRow({ label, value }: { label: string; value: ReactNode }) {
	return (
		<div className='grid grid-cols-[110px_1fr] items-baseline gap-3 py-1'>
			<dt className='text-[12px] text-gray-800'>{label}</dt>
			<dd className='min-w-0 truncate text-[13px] text-ink'>{value}</dd>
		</div>
	);
}

// ---------------------------------------------------------------------------------------------
// Replies
// ---------------------------------------------------------------------------------------------

type ReplyView = 'sentences' | 'words';

const SENTENCE_CLASS: Record<SentenceChange, string> = {
	kept: '',
	reworded: 'rounded-[3px] bg-amber-50 shadow-[inset_0_-1px_0] shadow-amber-500',
	removed: 'rounded-[3px] bg-red-50 text-red-600 line-through',
	added: 'rounded-[3px] bg-green-100 text-green-600'
};

const WORD_CLASS = { removed: SENTENCE_CLASS.removed, added: SENTENCE_CLASS.added };

function Legend({ changes }: { changes: SentenceChange[] }) {
	return (
		<>
			{changes.map((change) => (
				<span key={change} className={cn('px-1', SENTENCE_CLASS[change])}>
					{change === 'removed' ? 'not kept' : change}
				</span>
			))}
		</>
	);
}

/** One reply with what the other does not have marked, by sentence or by word. */
function MarkedReply({
	label,
	text,
	otherText,
	side,
	view
}: {
	label: string;
	text: string | null;
	otherText: string;
	side: 'removed' | 'added';
	view: ReplyView;
}) {
	const legend: SentenceChange[] = view === 'sentences' ? ['reworded', side] : [side];
	return (
		<section aria-label={label} className='flex min-w-0 flex-col gap-1.5'>
			<p className='flex flex-wrap items-center gap-1.5 text-[12px] text-gray-800'>
				{label} · <Legend changes={legend} />
			</p>
			<p className={TEXT_BOX_CLASS}>
				{!text
					? 'No text was kept.'
					: view === 'sentences'
						? markSentenceChanges(text, otherText, side).map((segment, index) =>
								segment.change === 'kept' ? (
									segment.text
								) : (
									<mark key={index} className={SENTENCE_CLASS[segment.change]}>
										{segment.text}
									</mark>
								)
							)
						: markWordChanges(text, otherText).map((segment, index) =>
								segment.changed ? (
									<mark key={index} className={WORD_CLASS[side]}>
										{segment.text}
									</mark>
								) : (
									segment.text
								)
							)}
			</p>
		</section>
	);
}

function Replies({ detail }: { detail: DeviationDetail }) {
	const [view, setView] = useState<ReplyView>('sentences');
	const toggle = (value: ReplyView, label: string) => (
		<button
			type='button'
			aria-pressed={view === value}
			onClick={() => setView(value)}
			className={cn('h-6 rounded-full px-3 text-[12px]', view === value ? 'bg-ink text-white' : 'text-gray-900 hover:bg-ink/4')}
		>
			{label}
		</button>
	);
	return (
		<Section
			label='Replies'
			aside={
				<div className='flex items-center gap-1 rounded-full border border-gray-300 p-0.5' role='group' aria-label='Compare by'>
					{toggle('sentences', 'Sentences')}
					{toggle('words', 'Words')}
				</div>
			}
		>
			<div className='grid grid-cols-2 gap-3'>
				<MarkedReply label='AI draft' text={detail.draft} otherText={detail.sentText ?? ''} side='removed' view={view} />
				<MarkedReply label='Sent' text={detail.sentText} otherText={detail.draft ?? ''} side='added' view={view} />
			</div>
			<p className='text-[12px] text-gray-800'>
				{detail.sentVia === 'extension'
					? 'Sent: the extension composer when the agent clicked Send.'
					: 'Sent: what the Sentinel workspace sent.'}
			</p>
		</Section>
	);
}

// ---------------------------------------------------------------------------------------------
// Sources and scores
// ---------------------------------------------------------------------------------------------

function SourcesUsed({ knowledgeBaseIds }: { knowledgeBaseIds: string[] }) {
	// The draft's retrieved knowledge only: the workflows it followed are not a source.
	const sources = useResponseSources({ knowledgeBaseIds, ruleIds: [] });
	const unnamed = knowledgeBaseIds.length - sources.length;
	return (
		<Section label='Sources used'>
			{knowledgeBaseIds.length === 0 ? (
				<p className='text-[13px] text-gray-800'>The draft used no sources.</p>
			) : (
				<div className='flex flex-wrap items-center gap-1.5'>
					{sources.map((source) =>
						source.url ? (
							<Link
								key={source.id}
								to={source.url}
								className='rounded-full border border-gray-300 px-2.5 py-1 text-[12px] text-ink hover:bg-ink/4'
							>
								{source.title}
							</Link>
						) : (
							<span key={source.id} className='rounded-full border border-gray-300 px-2.5 py-1 text-[12px] text-ink'>
								{source.title}
							</span>
						)
					)}
					{unnamed > 0 && (
						<span className='text-[12px] text-gray-800'>
							{sources.length > 0 ? `+${unnamed} more` : `${unnamed} ${unnamed === 1 ? 'source' : 'sources'}`} not named
						</span>
					)}
				</div>
			)}
		</Section>
	);
}

/** One figure of the Scores card. */
function Figure({ label, value, note }: { label: string; value: ReactNode; note?: string }) {
	return (
		<div className='flex min-w-0 flex-col gap-1 px-3 py-2.5'>
			<p className='text-[12px] text-gray-800'>{label}</p>
			<div className='text-[20px] font-medium leading-6 tabular-nums text-ink'>{value}</div>
			{note && <p className='text-[11px] text-gray-800'>{note}</p>}
		</div>
	);
}

function FactorMeter({ level }: { level: number }) {
	return (
		<span className='flex gap-[3px]' aria-hidden='true'>
			{[1, 2, 3, 4, 5].map((step) => (
				<span
					key={step}
					className={cn('h-1.5 w-[18px] rounded-[2px]', step > level ? 'bg-gray-300' : level <= LOW_FACTOR_LEVEL ? 'bg-red-500' : 'bg-ink')}
				/>
			))}
		</span>
	);
}

function Scores({ detail }: { detail: DeviationDetail }) {
	const kinds = [...new Set(detail.writingIssues.map((issue) => WRITING_KIND_LABELS[issue.kind]))];
	const scoreNote =
		detail.aiScore == null
			? 'The AI check did not score this reply'
			: detail.aiScore < detail.blockBelow
				? `Under the ${detail.blockBelow} threshold`
				: `At or above ${detail.blockBelow}`;
	return (
		<Section label='Scores'>
			<div className={cn(BOX_CLASS, 'grid grid-cols-3 divide-x divide-gray-300')}>
				<Figure label='AI score' value={<AiScoreCell aiScore={detail.aiScore} blockBelow={detail.blockBelow} />} note={scoreNote} />
				<Figure label='Writing issues' value={detail.writingIssues.length} note={kinds.join(', ') || 'None'} />
				<Figure
					label='Edit %'
					value={detail.editRate == null ? '—' : `${detail.editRate}%`}
					note={
						detail.sharedWords != null && detail.totalWords != null
							? `${detail.sharedWords} of ${detail.totalWords} words shared`
							: undefined
					}
				/>
			</div>
			{detail.factors.length > 0 && (
				<ul className='flex flex-col gap-2 pt-1' aria-label='Factors'>
					{detail.factors.map((factor) => (
						<li key={factor.key} className='grid grid-cols-[minmax(0,1fr)_auto_32px] items-center gap-2.5 text-[13px] text-ink'>
							<span title={factor.levelText}>{factor.label}</span>
							<FactorMeter level={factor.level} />
							<span className='text-right tabular-nums text-gray-800'>{factor.level}/5</span>
						</li>
					))}
				</ul>
			)}
			{detail.writingIssues.length > 0 && (
				<ul className='flex flex-col gap-1.5 pt-1' aria-label='Writing issues'>
					{detail.writingIssues.map((issue, index) => (
						<li key={index} className='flex flex-wrap items-center gap-2 text-[13px]'>
							<StatusTag tone='amber'>{WRITING_KIND_LABELS[issue.kind]}</StatusTag>
							<span className='text-ink'>{issue.text}</span>
						</li>
					))}
				</ul>
			)}
		</Section>
	);
}

// ---------------------------------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------------------------------

function ReviewForm({ detail }: { detail: DeviationDetail }) {
	const { can } = usePermissions();
	// Whoever can open Logs › Deviations (`reports.view`, admins) reviews it: no role holds `reports.edit`.
	const canReview = can('view', 'reports');
	const save = useSaveDeviationReview();
	const [verdict, setVerdict] = useState<DeviationReviewVerdict | null>(detail.review?.verdict ?? null);
	const [note, setNote] = useState(detail.review?.note ?? '');
	const [saved, setSaved] = useState(false);
	const [failed, setFailed] = useState(false);

	const onSave = () => {
		if (!verdict) return;
		setSaved(false);
		setFailed(false);
		save.mutate(
			{ checkId: detail.checkId, verdict, note: note.trim() || null },
			{ onSuccess: () => setSaved(true), onError: () => setFailed(true) }
		);
	};

	return (
		<Section label='Your verdict'>
			{detail.review && (
				<p className='text-[12px] text-gray-800'>
					Reviewed by {detail.review.reviewedBy ?? 'a former user'} · {format(new Date(detail.review.reviewedAt), 'd MMM yyyy, HH:mm')}
				</p>
			)}
			<div className={cn(BOX_CLASS, 'flex flex-col divide-y divide-gray-300 overflow-hidden')} role='radiogroup' aria-label='Your verdict'>
				{DEVIATION_REVIEW_VERDICTS.map((choice) => (
					<label
						key={choice}
						className={cn(
							'grid cursor-pointer grid-cols-[auto_minmax(0,1fr)] gap-x-2.5 gap-y-0.5 px-3 py-2.5',
							verdict === choice && 'bg-ink/4',
							canReview ? 'hover:bg-ink/4' : 'cursor-default opacity-80'
						)}
					>
						<input
							type='radio'
							name={`verdict-${detail.checkId}`}
							value={choice}
							checked={verdict === choice}
							disabled={!canReview}
							onChange={() => setVerdict(choice)}
							className='mt-0.5 accent-ink'
						/>
						<span className={cn('text-[13px] text-ink', verdict === choice && 'font-medium')}>{VERDICT_CHOICES[choice].title}</span>
						<span className='col-start-2 text-[12px] text-gray-800'>{VERDICT_CHOICES[choice].hint}</span>
					</label>
				))}
			</div>
			{canReview ? (
				<div className='flex flex-col gap-3 pt-1'>
					<label className='flex flex-col gap-1.5 text-[12px] font-medium text-ink'>
						Note (optional)
						<Textarea
							value={note}
							onChange={(e) => setNote(e.target.value)}
							maxLength={MAX_NOTE_LENGTH}
							rows={2}
							placeholder='Anything the lead should know'
							className='text-[13px] font-normal'
						/>
					</label>
					<div className='flex flex-wrap items-center justify-end gap-3'>
						{saved && (
							<span role='status' className='text-[12px] text-green-600'>
								Saved
							</span>
						)}
						{failed && (
							<span role='alert' className='text-[12px] text-red-600'>
								The verdict could not be saved. Try again.
							</span>
						)}
						<Button size='sm' className='rounded-full' onClick={onSave} disabled={!verdict || save.isPending}>
							{save.isPending ? 'Saving…' : 'Save verdict'}
						</Button>
					</div>
				</div>
			) : (
				<p className='text-[12px] text-gray-800'>
					{detail.review?.note ? `Note: ${detail.review.note}` : detail.review ? 'No note.' : 'Not reviewed yet.'}
				</p>
			)}
		</Section>
	);
}

// ---------------------------------------------------------------------------------------------
// The drawer
// ---------------------------------------------------------------------------------------------

export function DeviationDetails({ deviation }: { deviation: Deviation }) {
	const { detail, isLoading, isError, refetch } = useDeviationDetail(deviation.checkId);
	if (isLoading) return <p className='px-5 py-6 text-[13px] text-gray-800'>Loading…</p>;
	if (isError || !detail) {
		return (
			<div className='flex flex-col items-start gap-3 px-5 py-6'>
				<p className='text-[13px] text-gray-900'>This deviation could not be loaded.</p>
				<Button variant='outline' size='sm' onClick={() => void refetch()}>
					Retry
				</Button>
			</div>
		);
	}
	return (
		<div className='flex flex-col divide-y divide-gray-300 pb-2'>
			<div className='flex flex-col gap-3 px-5 py-4'>
				{detail.overrideReason && (
					<section
						aria-label='Why it was sent anyway'
						className='flex flex-col gap-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5'
					>
						<p className='text-[12px] font-medium text-amber-700'>Why it was sent anyway</p>
						<p className='whitespace-pre-wrap text-[13px] leading-[18px] text-ink'>{detail.overrideReason}</p>
					</section>
				)}
				<dl>
					<MetaRow label='Intent' value={detail.intent ? <span className='capitalize'>{humanize(detail.intent)}</span> : '—'} />
					<MetaRow label='Channel' value={<span className='capitalize'>{detail.channel}</span>} />
					<MetaRow label='Sent by' value={detail.sentBy ?? 'Unknown sender'} />
					<MetaRow label='Sent from' value={SENT_VIA_LABELS[detail.sentVia]} />
					<MetaRow label='Sent at' value={format(new Date(detail.sentAt), 'd MMM yyyy, HH:mm:ss')} />
				</dl>
			</div>
			<Section label='Customer asked'>
				<p className={TEXT_BOX_CLASS}>{detail.customerMessage?.text ?? 'The customer message is not stored.'}</p>
			</Section>
			<Replies detail={detail} />
			<SourcesUsed knowledgeBaseIds={detail.knowledgeBaseIds} />
			<Scores detail={detail} />
			<ReviewForm detail={detail} />
		</div>
	);
}
